import { useCallback } from 'react';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { useIsMobileScreen } from '@/hooks/useMedia';

import { getCurrencyISO } from './utils';

import { useTickerSearch } from './useTickerSearch';

import { ComboBox } from '@simplywallst/ui-core/next';

import { PrimaryListing } from './PrimaryListing';
import { SecondaryListing } from './SecondaryListing';

import { MobileField } from './MobileField';

import { isCurrencyISO } from '@/constants/currencies';

type Value = {
  symbol: string;
  exchangeCode: string;
  companyName: string;
  currencyISO: ReturnType<typeof getCurrencyISO>;
};

type Props = {
  value: Value | null;
  isDisabled?: boolean;
  onChange: (value: Value | null) => void;
  error: string | null;
};

export const TickerSearch = ({
  value,
  error,
  isDisabled = false,
  onChange,
}: Props) => {
  const { symbol, exchangeCode, companyName } = value || {};

  const { searchQuery, setSearchQuery, listings, status } = useTickerSearch();

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleChange = useCallback(
    (id: string | null) => {
      if (id === null) {
        onChange(null);
        return;
      }

      const selectedCompany = listings.find((listing) => listing.id === id);

      if (!selectedCompany) return;

      const [exchangeCode, symbol] = selectedCompany.uniqueSymbol.split(':');

      const currencyISO =
        selectedCompany.type === 'secondary' &&
        isCurrencyISO(selectedCompany.listingCurrencyIso)
          ? selectedCompany.listingCurrencyIso
          : getCurrencyISO(selectedCompany.exchangeCountryIso || 'us');

      onChange({
        symbol,
        exchangeCode,
        companyName: selectedCompany.name,
        currencyISO,
      });
    },
    [onChange, listings]
  );

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  return isMobileScreen ? (
    <MobileField
      label="Ticker"
      value={value ? `${exchangeCode}:${symbol} - ${companyName}` : ''}
      placeholder="Search for a ticker"
      error={error}
      options={listings}
      inputValue={searchQuery}
      status={status}
      isDisabled={isDisabled}
      onSelect={handleChange}
      onInputChange={setSearchQuery}
    />
  ) : (
    <ComboBox
      label="Ticker"
      placeholder="Search for a ticker"
      options={listings}
      error={error}
      isLoading={Boolean(Number(searchQuery?.length) && status === 'pending')}
      isDisabled={isDisabled}
      value={
        isDisabled ? `${exchangeCode}:${symbol} - ${companyName}` : searchQuery
      }
      onSearch={handleSearch}
      onChange={handleChange}
    >
      {(item) => (
        <ComboBox.Option
          textValue={item.label}
          key={item.id}
          className="min-w-[380px] border-0 border-b border-solid border-subtle"
        >
          {item.type === 'primary' ? (
            <PrimaryListing {...item} />
          ) : (
            <SecondaryListing {...item} />
          )}
        </ComboBox.Option>
      )}
    </ComboBox>
  );
};
